import { Fragment, useEffect, useRef, useState } from "react";

import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions, Label, Transition } from "@headlessui/react";

import useCategoriesPartial from "../../../data/useCategoriesPartial";
import { IPartCategory } from "../../../models/Part";
import classNames from "../../../utils/classNames";
import LoadingWheel from "../../shared/LoadingWheel";


interface PartCategoryComboProps {
  handleAddCategory: any;
  placeholder?: string;
  shouldFocus?: boolean;
}

export default function PartCategoryCombo({ handleAddCategory, placeholder = "Start typing to search for a category", shouldFocus = false }: PartCategoryComboProps) {
  const { categories: allCategories } = useCategoriesPartial();
  const [searchTerm, setSearchTerm] = useState<any>("");
  const [categories, setCategories] = useState<IPartCategory[]>(allCategories!.data?.data || []);

  useEffect(() => {
    if (!allCategories.isLoading) {
      setCategories(allCategories.data?.data || []);
    }

    if (!allCategories.isLoading && searchTerm?.length > 0) {
      setCategories(
        allCategories.data?.data.filter((category) =>
          category.name.toLowerCase().includes(searchTerm.toLowerCase())
        ) || []
      );
    }
  }, [allCategories.isLoading, allCategories.data, searchTerm]);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  if (allCategories.isLoading) {
    return <LoadingWheel />;
  }

  return (
    <div className="w-full">
      <Combobox immediate onChange={(category: IPartCategory) => handleAddCategory(category)} >
        {({ open }) => (
          <>
            <Label className="block text-sm font-medium text-gray-500">
              Part Category
            </Label>
            <div className="relative mt-1">
              <ComboboxInput
                ref={shouldFocus ? inputRef : null}
                className="w-full border border-gray-400 bg-white py-2 pl-3 pr-10 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm"
                onChange={(e) =>
                  setSearchTerm(e.target.value)
                }
                displayValue={searchTerm}
                placeholder={placeholder}
                autoComplete="off"
              />
              <Transition
                show={open}
                appear={true}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <ComboboxOptions
                  static
                  className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"
                >
                  {categories && categories.length! > 0 && searchTerm.length > 0 ? (
                    categories.slice(0, 50).map((category: IPartCategory, i: number) => (
                      <ComboboxOption
                        className={({ selected, focus }) =>
                          classNames(
                            selected
                              ? "text-white bg-primary-600"
                              : "text-gray-900",
                            focus ? "cursor-pointer bg-primary-100" : "cursor-default",
                            "cursor-default select-none relative py-2 pl-8 pr-4"
                          )
                        }
                        key={i}
                        value={category}
                      >
                        <span className="block truncate">
                          {category.name}
                        </span>
                      </ComboboxOption>
                    ))
                  ) : (
                    <ComboboxOption
                      disabled
                      className="cursor-default select-none relative py-2 text-center text-gray-500"
                      value={null}
                    >
                      No categories match the search terms
                    </ComboboxOption>
                  )}
                </ComboboxOptions>
              </Transition>
            </div>
          </>
        )}
      </Combobox>
    </div>
  )
}